/**
 * spec グラフ読取 API が共有する語彙と部品スキーマ。
 *
 * ノード型・エッジ型・アクセス種別・接地種別・フローノード種別・変更対象種別・導出操作は
 * すべて `@cortex/domain-spec-graph`（共通部の契約）から引く。読取側で同じ allowlist を
 * 書き直すと、語彙の追加が片側だけに入って 2 表現が乖離するためである（core-principles の
 * 一次表現原則・制約 11）。
 *
 * 導出できる値は持たせない。出自の日本語ラベル（`SPEC_EDGE_ORIGIN_LABELS_JA`）と接地状態
 * （`deriveGroundingStatus`）はレスポンスに載せず、この module が公開する写像で UI 側が
 * 引く。同じ事実をレスポンスの 2 フィールドに載せると、片方だけ古い値のまま配信できてしまう。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business spec グラフ読取 API の共有語彙（ノード型・エッジ出自・違反・ページング・用語出現）を 1 箇所に集約する。
 * @graph-connects none
 */
import { ACCESS_MODES, DATA_ACCESS_KINDS, FLOW_NODE_TYPES, IMPLEMENTATION_KINDS, SOURCE_LOCALE, SPEC_CONSTRAINT_RULES, SPEC_EDGE_TYPES, OPERATION_NODE_KINDS, SPEC_LOCALES, SPEC_NODE_TYPES, TERM_MATCH_KINDS as SPEC_GRAPH_TERM_MATCH_KINDS, } from '@cortex/domain-spec-graph';
import { z } from 'zod';
/**
 * 本文を揃える locale の一覧と原文の locale。共通部の宣言をそのまま再公開する。
 *
 * 画面の言語切替が `['ja', 'en', 'vi']` を手で持つと、入力スキーマ・MCP 書込・画面の 3 経路が
 * 別々の一覧を持つことになり、経路ごとに揃う言語が変わる（制約 11 — 共通部 `locales.ts` の
 * docblock が名指しで禁じている状態）。かといって画面が `@cortex/domain-spec-graph` に直接
 * 依存すると、書込コアと BigQuery クライアントがブラウザバンドルへ入る。読取契約はその両方を
 * 避けられる唯一の経路なので、ここが値を**書き直さず素通しする**。
 *
 * 値を再宣言しないこと自体が要件である。`['ja', 'en', 'vi']` とここに書いた時点で、共通部と
 * 読取契約の 2 表現になり、言語を足した日に片方が取り残される。
 * @graph-connects none
 */
export { SOURCE_LOCALE, SPEC_LOCALES } from '@cortex/domain-spec-graph';
/**
 * 本文 1 つ × 用語 1 件の出現を位置順に返す共通部の判定を、画面へ素通しする。
 *
 * **画面が自前で「含まれているか」を書いてはならない。** 同じ判定が USES_TERM エッジの
 * 抽出（どの業務がどの用語を使っているか）にも使われており、書き写すと抽出とリンクが
 * 別の規則で動く。そうなると「使用箇所には数えられているのに本文ではリンクされない」語が
 * 生まれ、しかも見た目は正常なので誰も気付けない。
 *
 * 素通しできるのは純関数だからである（BQ に触れない module に置いてある）。これを
 * `@cortex/domain-spec-graph` から画面が直に引くと、書込コアと BigQuery クライアントが
 * ブラウザバンドルへ入る。
 * @graph-connects none
 */
export { termOccurrencesIn } from '@cortex/domain-spec-graph';
/**
 * 要求の文を見出しの長さへ切り詰める共通部の関数を、画面へ素通しする。
 *
 * **画面が上限の字数と数え方を自前で持ってはならない。** 書込の上限（共通部の
 * `REQUIREMENT_STATEMENT_MAX_LENGTH`）と画面の切り詰めが別の値・別の数え方で動くと、書込を
 * 通った文が画面では切られる（またはその逆）。切り詰めは上限の値と書記素の数え方を共通部から
 * 引くので、上限を変えた日に画面も追随する。素通しできるのは BQ に触れない純関数だからである。
 * @graph-connects none
 */
export { truncateRequirementStatement } from '@cortex/domain-spec-graph';
/**
 * エッジの出自 3 種（大枠設計 §7 の表示パターン #2「出自ラベル」）。
 *
 * 値は共通部の `SpecEdgeOrigin` に一致させる。共通部は runtime の配列を持たないため
 * ここで宣言し、`satisfies` と契約テストで `SPEC_EDGE_DEFINITIONS` の実値と突合する。
 * @graph-connects none
 */
export const SPEC_EDGE_ORIGINS = [
    'path-or-definition',
    'declaration',
    'derivation',
];
/**
 * 出自の日本語表示ラベル。レスポンスには載せず、UI がこの写像で引く。
 * @graph-connects none
 */
export const SPEC_EDGE_ORIGIN_LABELS_JA = {
    'path-or-definition': 'パス・定義由来',
    declaration: '宣言由来',
    derivation: '導出',
};
/**
 * 接地宣言の相手が SPG（実装レポ索引）に在るか。
 *
 * `never-existed` は宣言が実装に先行している状態（決定 aa の実装待ち）、`removed` は
 * 実装側の改名・削除で宙吊りになった状態である。
 * @graph-connects none
 */
export const SPG_PRESENCES = ['present', 'never-existed', 'removed'];
/**
 * 接地の健全性。`spgPresence` から一意に決まるため**レスポンスには載せない**。
 *
 * `unverified` は SPG 索引と突き合わせていない状態（`spgPresence` が `null`）である。
 * 残る 3 値は突合の**結果**なので、突合していない状態をそのどれかに畳むと「実装がある」
 * または「実装待ちである」と嘘をつくことになる。判定していない事実を別の値で表す。
 * @graph-connects none
 */
export const IMPLEMENTATION_GROUNDING_STATUSES = [
    'grounded',
    'implementation-waiting',
    'dangling',
    'unverified',
];
/**
 * SPG 実在状態から接地の健全性を導出する。監査バッジの表示分岐はこの 1 箇所だけが決める。
 *
 * `null`（SPG と未照合）も受ける。UI 側で `null` を先に弾いてから呼ぶ形にすると、
 * 「未照合をどう描くか」の判断が呼び出し側の数だけ増え、画面ごとに違う扱いになる。
 * @graph-connects none
 */
export function deriveGroundingStatus(presence) {
    if (presence === null)
        return 'unverified';
    if (presence === 'present')
        return 'grounded';
    if (presence === 'never-existed')
        return 'implementation-waiting';
    return 'dangling';
}
/**
 * 用語がどの表記で本文に一致したか。`synonym` の一致だけが語彙違反（制約 12）になる。
 *
 * 共通部の allowlist をそのまま再公開する。正表記は本文 1 組の locale ごとにあるため、
 * 一致表記の種別は locale の一覧から導かれる（`[...SPEC_LOCALES, 'synonym']`）。ここで
 * 独自の配列を宣言すると、揃える言語を足したときに増えた言語で当たった出現が読取側で
 * 黙って `synonym` に落ち、語彙違反でないものが違反として出る。
 * @graph-connects none
 */
export const TERM_MATCH_KINDS = SPEC_GRAPH_TERM_MATCH_KINDS;
/**
 * 読取が本文を返す言語。
 *
 * 共通部の `SPEC_LOCALES` をそのまま使う。**読取は 1 locale だけを返す**（書込を担う MCP が
 * 全 locale を扱うのと非対称である）。読み手は人であり、3 言語を並べた本文は読む側が
 * 自分の言語を毎回選び直すことになるためで、選択は入力で 1 度だけ行う。
 * @graph-connects none
 */
export const SpecLocaleSchema = z.enum(SPEC_LOCALES);
/**
 * 本文を返す読取が共通で受け取る言語指定。既定は原文の locale である。
 *
 * 既定を原文（`SOURCE_LOCALE`）にするのは、原文だけが必ず埋まっている言語だからである。
 * 派生言語を既定にすると、訳が入っていないノードを開いた読み手が既定のまま空の画面を見る。
 * @graph-connects none
 */
export const LocaleInputShape = {
    locale: SpecLocaleSchema.default(SOURCE_LOCALE),
};
/**
 * 選んだ言語に本文が入っていないか。
 *
 * `true` は「原文はあるが、選んだ言語の訳がまだ無い」を表す。**未翻訳を原文で埋めない**の
 * がこの旗の存在理由である（決定 4）。空欄の代わりに日本語を出すと、読み手には訳が済んだ
 * 本文と区別が付かず、翻訳の残作業が画面から永久に見えなくなる。
 *
 * 原文そのものが空（詳細未投入 — 決定 ab）のときは `false` である。書かれていない本文は
 * 訳しようがなく、未翻訳として数えると翻訳の残作業に未着手の仕様が混ざる。
 * @graph-connects none
 */
export const UntranslatedSchema = z.boolean();
/**
 * マスター detail が扱う分類マスターの型（設計書 api §4.0 の `getMasterDetail` の対象）。
 *
 * 綴りはルートのパスパラメータ（`/masters/$type/$id`）と同じ snake_case で、共通部の
 * `MASTER_NODE_TYPES` と 1 対 1 に対応する。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスター detail が扱う分類マスターの型（設計書 api §4.0 の getMasterDetail の対象）
 * @graph-connects none
 */
export const MASTER_DETAIL_TYPES = ['actor', 'domain', 'stage', 'verb', 'unit'];
/** spec グラフのノード型。 @graph-connects none */
export const SpecNodeTypeSchema = z.enum(SPEC_NODE_TYPES);
/** spec グラフのエッジ型。 @graph-connects none */
export const SpecEdgeTypeSchema = z.enum(SPEC_EDGE_TYPES);
/** エッジの出自。 @graph-connects none */
export const SpecEdgeOriginSchema = z.enum(SPEC_EDGE_ORIGINS);
/** `usesColumn` のアクセス種別。 @graph-connects none */
export const AccessModeSchema = z.enum(ACCESS_MODES);
/** Operation のデータアクセス種別（DB を触るか触らないかの判別子）。 @graph-connects none */
export const DataAccessKindSchema = z.enum(DATA_ACCESS_KINDS);
/** 接地宣言の種別。 @graph-connects none */
export const ImplementationKindSchema = z.enum(IMPLEMENTATION_KINDS);
/**
 * 操作の種別（normal / precondition）。共通部の allowlist を写す。
 *
 * 前提（precondition）は「手前で済んだ業務」を参照するだけの節点で、本文・部品・実装先を
 * 持たない（SR-04）。画面はこの値で描き分ける。
 * @graph-connects none
 */
export const OperationNodeKindSchema = z.enum(OPERATION_NODE_KINDS);
/** フローノードの種別。 @graph-connects none */
export const FlowNodeTypeSchema = z.enum(FLOW_NODE_TYPES);
/** SPG 上の実在状態。 @graph-connects none */
export const SpgPresenceSchema = z.enum(SPG_PRESENCES);
/** 用語の一致表記。 @graph-connects none */
export const TermMatchKindSchema = z.enum(TERM_MATCH_KINDS);
/** マスター detail の対象種別。 @graph-connects none */
export const MasterDetailTypeSchema = z.enum(MASTER_DETAIL_TYPES);
/** ISO 8601（オフセット付き）の日時。 @graph-connects none */
export const IsoDateTimeSchema = z.iso.datetime({ offset: true });
/**
 * ノードの最終更新時刻。**不明を `null` で表す**。
 *
 * `spec_graph_nodes.updatedAt` は BQ 上 nullable で、共通部の読取は
 * 欠損を空文字へ落とす。空文字は ISO 8601 として不正なので、非 null 必須のままにすると
 * 移行中の書きかけ 1 行で detail の読取が 500 になり、**画面から不備を見に行けなくなる**
 * （`GraphSourcedStringSchema` と同じ理由・決定 ab）。空文字を日時として通す道は無いため、
 * ラベルと違って `null` で表す。
 *
 * `null` は「更新時刻が入っていない」であって「更新されていない」ではない。UI は
 * 「更新日時なし」と描き、時刻の代わりに 0 や現在時刻を埋めない。
 * @graph-connects none
 */
export const NodeUpdatedAtSchema = IsoDateTimeSchema.nullable();
/** 空でない ID・ラベル文字列。 @graph-connects none */
export const NonEmptyStringSchema = z.string().min(1);
/**
 * 作り直しの要素 1 件を指す番号。全型で 1 本の連番で、**1 から始まる**（決定 1）。
 *
 * 0 と負の数を弾く。0 は「未設定」の見た目を持ち、負の数は 1 回の書込の中だけで通じる仮の
 * 番号である。どちらも応答に出てよい値ではない。
 *
 * **ここに 1 本だけ置く。** 要素の番号は処理フローの契約（`spec-graph-rule-flow.ts`）と監査の
 * 契約（`spec-graph-element-audit.ts`）の両方が使うので、機能ごとに宣言すると同じ名前が
 * `index.ts` の `export *` で衝突し、パッケージのビルドが落ちる（実際に落ちた）。
 * @graph-connects none
 */
export const SpecElementIdSchema = z.number().int().positive();
/**
 * 仕様グラフの nullable な列に由来する文字列。空文字も正当な値として受ける。
 *
 * `spec_graph_nodes` の `qualifiedName` / `properties` と `spec_graph_edges` の `properties` は
 * BQ 上 nullable で、読取は欠損を空文字へ落とす。ここを非空必須にすると、移行中の書きかけ
 * （決定 ab の「書きかけは正常」）に当たった瞬間に読取が 500 を返し、**画面から不備を見に
 * 行けなくなる**。監査は 0 を潰さず見せるための機能なので、空を弾く側へ倒さない。
 *
 * 非空を守れる値（入力のエコー・BFF が組み立てる導出値・BQ の非 nullable 列）は
 * `NonEmptyStringSchema` のままにする。どちらの理由でその形なのかを読み手が区別できるよう、
 * 別名で宣言して混ぜない。
 * @graph-connects none
 */
export const GraphSourcedStringSchema = z.string();
/**
 * Term の揺れ表記 1 件。
 *
 * 共通部の入力スキーマ（`synonymSchema`）は使わない。あちらの `note` は全 locale が揃った
 * 本文 1 組で、読取は 1 locale に畳んだ値を返すためである。`text` は綴りの揺れそのもので
 * 訳語ではないため、畳む対象にならない。
 *
 * `untranslated` を持たないのは、`note` を解決できる読取が共通部に無いためである（表記索引は
 * 表記文字列しか持たない）。常に `null` になる欄に未翻訳の旗を立てると、訳が無いことと
 * 読取が解決していないことが同じ値で表される。
 * @graph-connects none
 */
export const SynonymSchema = z.object({
    text: NonEmptyStringSchema,
    note: z.string().nullable(),
});
/** ページングの不透明カーソル。中身の形式は BFF の実装詳細で、client は再送するだけである。 @graph-connects none */
export const CursorSchema = z.string().min(1);
/**
 * 違反の重さ。**誰が消すか**で 2 段に分かれる。`error` は仕様が未完成（仕様作成者が消す）、
 * `warning` は実装との紐づけが未登録（実装者が消す）。画面はこの値で色を分ける。
 *
 * 共通部（`@cortex/domain-spec-graph`）にも同じ 2 値があるが、UI 契約は共通部に依存しない
 * 形で閉じている（web からは共通部を引けない）。値が増えるときは両方に足す。
 * @graph-connects none
 */
export const VIOLATION_SEVERITIES = ['error', 'warning'];
/** 違反の重さ。 @graph-connects none */
export const ViolationSeveritySchema = z.enum(VIOLATION_SEVERITIES);
/**
 * 制約 32 項目（2・5・12・18・42〜69）の違反 1 件。
 *
 * 形は共通部の `ElementConstraintViolation`（宛先はノードの番号 `nodeId`）に `kind` を足した
 * ものである。書込で返る違反と読取で返る違反が同じ形なら、違反バッジの描画は 1 実装で足りる。
 *
 * `constraintNumber` の上限は契約側の `SPEC_CONSTRAINT_RULES` から**導出する**。数字を書くと
 * 制約を足したときに必ず追随を忘れ、新しい制約の違反だけが読取の入口で zod に弾かれて
 * 画面から消える（実際に制約 19〜24 を足したときに踏んだ）。落ちるのは違反の側なので、
 * 症状は「要確認が出ない」であり、規則を足した人からは最も見えにくい形で壊れる。
 * @graph-connects none
 */
export const ConstraintViolationSchema = z.object({
    kind: z.literal('violation'),
    constraintNumber: z
        .number()
        .int()
        .min(1)
        .max(Math.max(...SPEC_CONSTRAINT_RULES.map((rule) => rule.constraintNumber))),
    severity: ViolationSeveritySchema,
    ruleId: NonEmptyStringSchema,
    nodeId: z.number().int().positive(),
    message: NonEmptyStringSchema,
    fixHint: NonEmptyStringSchema.nullable(),
});
/**
 * 本文中の用語出現 1 件。`offset` / `length` は UTF-16 コード単位の位置で、UI はこの範囲を
 * リンク化する（`String.prototype.slice` にそのまま渡せる）。
 * @graph-connects none
 */
export const TermOccurrenceSchema = z.object({
    field: NonEmptyStringSchema,
    matchedText: NonEmptyStringSchema,
    matchedAs: TermMatchKindSchema,
    offset: z.number().int().min(0),
    length: z.number().int().min(1),
});
/**
 * detail 応答が返す用語解決 1 件（§7 の表示パターン #4「用語解決」）。
 * @graph-connects none
 */
export const ResolvedTermSchema = z.object({
    termId: GraphSourcedStringSchema,
    label: GraphSourcedStringSchema,
    definition: GraphSourcedStringSchema,
    untranslated: UntranslatedSchema,
    occurrences: z.array(TermOccurrenceSchema),
});
/**
 * 逆引きの 1 ページ（API §5 の「上位 N + カーソル」）。
 *
 * `total` は全件数、`topN` は 1 ページの上限、`items` はそのページ、`nextCursor` は続きの
 * 目印である。Actor ← 最大 600 / 人気 Term ← 数百の逆引きを全件返さないための共通の形で、
 * 逆引きを返す全クエリがこの 1 つの形を使う。
 * @graph-connects none
 */
export function pageSchema(item) {
    return z.object({
        total: z.number().int().min(0),
        topN: z.number().int().min(1),
        items: z.array(item),
        nextCursor: CursorSchema.nullable(),
    });
}
/**
 * 逆引きページングの共通入力。`limit` 未指定時の既定は BFF が決める。
 * @graph-connects none
 */
export const PageInputShape = {
    limit: z.number().int().min(1).max(200).optional(),
    cursor: CursorSchema.optional(),
};
//# sourceMappingURL=spec-graph-common.js.map